import { abi } from ".";
import { getConnectionInfo } from "@helpers";

import { getChainId } from "wagmi/actions";

import { wagmiConfig } from "@config";

import {
  lookupContract,
  lookupToken,
  type TokenDefinition,
  type ChainId,
} from "@bracket/addressbook";

import type { ContractProps, SmartContractConfig, Address } from "../types";

0;
/**
 * Deposit token(s) to Escrow
 * @param {Address} token the address of the token the deposit will emerge as
 * @param {WEI} amount the amount of token for this deposit
 * @returns
 */
export const depositToken =
  (chain: ChainId) =>
  (
    props: ContractProps = {}
  ): SmartContractConfig<"depositToken", typeof abi> => {
    throw new Error("deposit token is deprecated");
  };

/**
 * Deposit token(s) to Escrow
 * @param {TokenDefinition} token the address of the token the deposit will emerge as
 * @param {WEI} amount the amount of token for this deposit
 * @returns
 */
export const depositTokenCore = ({
  token,
  amount,
}: {
  token: TokenDefinition;
  amount: bigint;
}): SmartContractConfig<"depositToken", typeof abi> => {
  const { address, chain } = getConnectionInfo();
  const contractAddress = lookupContract(chain, "Escrow");
  const result = {
    address: contractAddress,
    abi,
    functionName: "depositToken",
    args: [address, token.address, amount],
  };

  const log = [
    "--- DEPOSIT ---",
    `chain: ${chain}`,
    `currency: ${token.label}`,
    `contract address: ${result.address}`,
    `functionName: ${result.functionName}`,
    `args:`,
    `\t0 [providing wallet address]: ${result.args[0]}`,
    `\t1 [token address]: ${result.args[1]}`,
    `\t2 [amount] ${result.args[2].toString()} (${
      Number(result.args[2]) / Number(1e18)
    })`,
    "----------",
  ];
  // console.log(log.join("\n"));
  return result;
};

/**
 * Get the balance an wallet/address
 * @param {Address} wallet the wallet address
 * @param {TokenDefinition} token the token/coin
 * @returns
 */
export const getUserBalance = (
  wallet: Address,
  token: TokenDefinition,
  chain: ChainId,
  options: { watch?: boolean } = {}
): SmartContractConfig<"getUserBalance", typeof abi> => {
  const address = lookupContract(chain, "Escrow");

  const result = Object.assign(
    {
      address,
      abi,
      functionName: "getUserBalance" as "getUserBalance",
      watch: true,
      args: [wallet, token.address],
      chainId: chain,
    },
    options
  );

  const log = [
    "--- GET_USER_BALANCE ---",
    `chain: ${chain}`,
    `currency: ${token}`,
    `called address: ${result.address}`,
    `functionName: ${result.functionName}`,
    `args:`,
    `\t0 [owner]: ${result.args[0]}`,
    `\t1 [token]: ${result.args[1]}`,
    "----------",
  ];
  // console.log(log.join("\n"));

  return result;
};

/**
 * Get information about a token
 * @param {ExecutableToken} token the token/coin
 * @returns
 */
// export const getTokenInfo = (chain: ChainId) => (token: TokenDefinition): SmartContractConfig<"getTokenInfo", typeof abi> => {
export const getTokenInfoOLD =
  (chain: ChainId) =>
  (token: string): SmartContractConfig<"getTokenInfo", any> => {
    if (typeof token === "string")
      throw new Error(
        `getTokenInfo received a token label ${token}. use TokenDefinition instance`
      );
    const address = lookupContract(chain, "Escrow");
    const result = {
      address,
      abi,
      functionName: "getTokenInfo",
      args: [token.address],
      chainId: chain,
    };
    return result;
  };

export const getTokenInfo = ({
  token,
  chain,
}: {
  token: TokenDefinition;
  chain: ChainId;
}): any => {
  const address = lookupContract(chain, "Escrow");
  const args = {
    address,
    abi,
    functionName: "getTokenInfo",
    args: [token.address],
    chainId: chain,
  };
  return args;
};

/**
 * Withdraw a token from esgrow
 *
 * @summary For rebase tokens the tokens will be unwrapped on their rebase version, therefore the input amount may differ from the transfered and returned amount
 * @param {ExecutableToken} token The address of the token to withdraw
 * @param {BigInt} amount the sum
 * @param {Boolean} unwrap set false for now.
 * @returns
 */
export const withdraw =
  (chain: ChainId) =>
  (props: ContractProps = {}): SmartContractConfig<"withdraw", typeof abi> => {
    const { token } = props;
    if (token) {
      const address = lookupContract(chain, "Escrow");
      const tokenAddress = lookupToken(chain, token);
      if (tokenAddress) {
        return Object.assign(props, {
          address,
          abi,
          functionName: "withdraw" as "withdraw",
        });
      }
      throw new Error(
        `Escrow::withdrawl was provided an invalid token  [${
          token || "no token"
        }:${tokenAddress || "unknown token address"}]`
      );
    }
    throw new Error(
      `Escrow::withdraw was provided an unknown token "${
        token || "no token set"
      }`
    );
  };

/**
 * Deposit token(s) to Escrow
 * @param {Address} token the address of the token the deposit will emerge as
 * @param {WEI} amount the amount of token for this deposit
 * @returns
 */
export const withdrawCore = ({
  token,
  amount,
}: {
  token: TokenDefinition;
  amount: bigint;
}): SmartContractConfig<"withdraw", typeof abi> => {
  try {
    const chain = getChainId(wagmiConfig);
    if (chain) {
      const address = lookupContract(chain, "Escrow");
      if (address) {
        return {
          address,
          abi,
          functionName: "withdraw",
          args: [token.address, amount, false],
        };
      }
      throw new Error(`failed finding Escrow contract on chain ${chain}`);
    }
    throw new Error("expected a valid chain but it was falsy");
  } catch (e) {
    const err =
      e instanceof Error ? e : { message: "no error instance resident" };
    throw new Error(
      `withdrawCore:failed building withdraw contract for token ${token.symbol} - ${err.message}`
    );
  }
};
